/**
 * Keeps the sealed session cookie in step with what the server actually holds.
 *
 * The UI reads `user.scheduled`, `user.sessionRegistration` and
 * `user.sessionWaitlist` from the cookie and nothing else. `refreshSession()`
 * re-decodes that cookie rather than reading a store, so a schedule changed on
 * another device, by an admin, or by the import task stays stale — indefinitely,
 * and a hard reload does not help, because SSR reads the same cookie.
 *
 * Until now the only thing that re-read the server was the My Schedule tab
 * watcher in agenda.vue. A tab parked on the sessions list or a session detail
 * page never updated at all.
 *
 * WHY THESE TRIGGERS. A poll would spend requests while nobody is looking; the
 * moments that matter are when someone comes back to the tab, and when a sibling
 * tab changes something. Both are free while idle.
 *
 * COST. hydrate reads the per-user KV copy of the schedule and the registration
 * KV entry, falling through to D1 only on a cache miss.
 */
// Imported rather than left to Nuxt's auto-import: the lifecycle hooks are the
// whole mechanism here, and an auto-import that resolves in the app but not
// under test would make this untestable in isolation. Nuxt dedupes the import.
import { onMounted, onUnmounted } from 'vue';

const CHANNEL = 'unbound:schedule';

// A floor between hydrates. Focus and visibilitychange both fire on a single
// alt-tab in some browsers, and returning to a tab should not cost two round
// trips.
export const MIN_INTERVAL_MS = 30_000;

/**
 * Spread the wake-up, PROPORTIONALLY TO HOW LONG THE TAB WAS AWAY.
 *
 * Attendees do not check their phones at random — they look when a session block
 * ends, together. A flat 3s spread meant ~2,700 hydrates/sec across an event of
 * this size, which is a self-inflicted version of the 2026-08-25 incident.
 *
 * But a long spread on a quick alt-tab feels broken, and a quick alt-tab is not
 * part of a herd: only tabs that have been hidden a while wake together. So the
 * spread scales with the hide. Someone flicking between windows gets an almost
 * immediate refresh; eight thousand phones coming out of pockets at the end of a
 * session are spread across a minute.
 */
export const JITTER_SHORT_MS = 2_000;
export const JITTER_LONG_MS = 60_000;
export const LONG_HIDE_MS = 60_000;
export const jitterFor = (hiddenMs) => (hiddenMs >= LONG_HIDE_MS ? JITTER_LONG_MS : JITTER_SHORT_MS);

// ── cross-tab channel ───────────────────────────────────────────────────────
// Module-level, not per-instance: there is one channel per TAB, and the
// mutation gateway below has to reach it without holding a component instance.

let channel = null;
const openChannel = () => {
    if (channel) return channel;
    try {
        channel = new BroadcastChannel(CHANNEL);
    } catch {
        // Safari private mode and older browsers. Focus still covers them; only
        // the instant cross-tab update is lost.
        channel = null;
    }
    return channel;
};

const closeChannel = () => {
    try { channel?.close(); } catch { /* already closed */ }
    channel = null;
};

/** Tell sibling tabs that this one changed the schedule. */
export const announceScheduleChange = () => {
    try { openChannel()?.postMessage('changed'); } catch { /* channel closed */ }
};

// ── mutation gate ───────────────────────────────────────────────────────────

/**
 * How many of the attendee's own schedule mutations are in flight.
 *
 * A focus hydrate that lands mid-mutation reads the pre-change caches and then
 * replaceUserSession writes THAT over the cookie — silently undoing the add the
 * person just made. Before this composable existed the window barely existed,
 * because hydrate only ran on the My Schedule tab; firing on focus opened it.
 *
 * Module-level so useUserApi can gate every mutation from one place rather than
 * threading a lock through each call site.
 */
let inFlight = 0;
export const mutationsInFlight = () => inFlight;
export const beginMutation = () => { inFlight += 1; };

/**
 * A mutation settled. Never below zero — a double-end from an error path must
 * not leave the gate permanently open.
 *
 * Announcing here rather than per-mutation: siblings care that the schedule
 * changed, not how many calls it took, and the cookie is only final once the
 * last one has written it.
 */
export const endMutation = () => {
    inFlight = Math.max(0, inFlight - 1);
    if (inFlight === 0) announceScheduleChange();
};

/** Test seam: the counter is module state and would leak between cases. */
export const __resetMutationGate = () => { inFlight = 0; closeChannel(); };

// ── the composable ──────────────────────────────────────────────────────────

export const useScheduleFreshness = () => {
    const { loggedIn, fetch: refreshSession } = useUserSession();

    let last = 0;
    let timer = null;
    let hiddenAt = 0;

    const hydrate = async () => {
        try {
            await $fetch('/api/v1/schedule/hydrate', { method: 'POST' });
            await refreshSession();
        } catch {
            // Best-effort by design: a failed hydrate leaves the existing cookie,
            // which is stale rather than wrong. Never surface this — the attendee
            // did not ask for it and cannot act on it.
        }
    };

    const request = ({ hiddenMs = 0 } = {}) => {
        if (!loggedIn.value) return;
        // Their own change is already being written to the cookie by the
        // mutation itself. Hydrating over it is how the add disappears.
        if (inFlight > 0) return;
        const now = Date.now();
        if (now - last < MIN_INTERVAL_MS) return;
        last = now;
        if (timer) clearTimeout(timer);
        timer = setTimeout(hydrate, Math.random() * jitterFor(hiddenMs));
    };

    const onVisible = () => {
        if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
        request({ hiddenMs: hiddenAt ? Date.now() - hiddenAt : 0 });
    };
    const onFocus = () => request({ hiddenMs: hiddenAt ? Date.now() - hiddenAt : 0 });

    /**
     * A sibling tab changed something.
     *
     * REFRESH ONLY — no hydrate. Cookies are not port- or tab-scoped, so the
     * sibling's mutation has ALREADY written the shared cookie; refreshSession
     * re-decodes exactly that. Hydrating here would re-read KV instead, and KV
     * is eventually consistent with no compare-and-swap — so a read that lost
     * the race would write the pre-change lists back over the cookie and undo
     * the sibling's change in the tab that made it. Costs nothing, and cannot
     * be stale: the cookie is the thing that just changed.
     */
    const onSiblingChange = () => { if (loggedIn.value) refreshSession(); };

    onMounted(() => {
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        const ch = openChannel();
        if (ch) ch.onmessage = onSiblingChange;
    });

    onUnmounted(() => {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', onFocus);
        if (timer) clearTimeout(timer);
        closeChannel();
    });

    return { hydrate, request, announce: announceScheduleChange };
};
